/**
 * 员工微信绑定 Modal（staff-rbac-wechat-login:SC-44~SC-46；IKBFM0 服务号真接）。
 *
 * 管理端为员工发起微信扫码绑定：
 * - 打开即调 generateBindQrcode 获取 {token, qrcodeUrl, mode, expiresAt}；
 * - embed 通道：iframe 嵌入微信扫码授权页（qrconnect，PC 直接扫；buildWechatEmbedUrl
 *   注入 self_redirect=true，确认后 iframe 内跳回调，绕开 Firefox 跨域顶级导航拦截）；
 * - qrcode 通道（服务号网页授权）：授权 URL 本地渲染二维码图，员工手机微信扫一扫确认；
 * - mock 通道（fake）：展示「模拟扫码」按钮，点击用固定 code 调回调端点模拟员工扫码
 *   （后端 fake OAuth 适配器已预置该 code 映射）；
 * - 每 2s 长轮询绑定状态，confirmed 后回调父组件刷新详情并关闭。
 */
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Space, Typography } from 'antd';
import { DraggableModal } from '../../../components/DraggableModal';
import { QrcodeImage } from '../../../components/QrcodeImage';
import { staffApi, type StaffDetail } from '../../../api/staff.api';
import { buildWechatEmbedUrl, resolveWechatQrcodeMode, type WechatQrcodeMode } from '../../../api/auth.api';

interface StaffWechatBindModalProps {
  /** 是否打开 */
  open: boolean;
  /** 待绑定员工（null 时不发起） */
  staff: StaffDetail | null;
  /** 关闭回调 */
  onClose: () => void;
  /** 绑定成功回调（父组件刷新详情） */
  onBound: () => void;
}

/** 绑定流程阶段 */
type Phase = 'loading' | 'binding' | 'confirmed' | 'expired' | 'failed';

const POLL_INTERVAL_MS = 2000;
const CONFIRMED_HOLD_MS = 1200;

export function StaffWechatBindModal({ open, staff, onClose, onBound }: StaffWechatBindModalProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [qrcodeUrl, setQrcodeUrl] = useState<string>('');
  const [qrMode, setQrMode] = useState<WechatQrcodeMode>('mock');
  const tokenRef = useRef<string>('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // onBound 用 ref 承载，避免父组件函数引用变化触发重新发起绑定
  const onBoundRef = useRef(onBound);
  onBoundRef.current = onBound;

  /** 清理轮询定时器 */
  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // 发起绑定 + 启动长轮询（仅依赖 open 与 staff.id，避免重复触发）
  useEffect(() => {
    if (!open || !staff) return;
    let cancelled = false;
    setPhase('loading');
    setQrcodeUrl('');

    const startPolling = (staffId: string, bindToken: string) => {
      clearTimer();
      timerRef.current = setInterval(async () => {
        try {
          const session = await staffApi.pollWechatBindSession(staffId, bindToken);
          if (cancelled) return;
          if (session.status === 'confirmed') {
            clearTimer();
            setPhase('confirmed');
            setTimeout(() => onBoundRef.current(), CONFIRMED_HOLD_MS);
          } else if (session.status === 'failed' || session.status === 'expired') {
            clearTimer();
            setPhase(session.status);
          }
        } catch {
          // 单次轮询网络异常忽略，等待下次重试
        }
      }, POLL_INTERVAL_MS);
    };

    void (async () => {
      try {
        const result = await staffApi.generateBindQrcode(staff.id);
        if (cancelled) return;
        tokenRef.current = result.token;
        setQrcodeUrl(result.qrcodeUrl);
        setQrMode(resolveWechatQrcodeMode(result.qrcodeUrl, result.mode));
        setPhase('binding');
        startPolling(staff.id, result.token);
      } catch {
        if (!cancelled) setPhase('failed');
      }
    })();

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [open, staff]);

  /** 模拟扫码（仅 fake 模式，用固定 code 调回调端点） */
  const handleMockScan = async () => {
    try {
      await staffApi.triggerWechatBindCallback(tokenRef.current);
    } catch {
      // 忽略：轮询会感知 session 状态变化
    }
  };

  const handleClose = () => {
    clearTimer();
    setPhase('loading');
    setQrcodeUrl('');
    tokenRef.current = '';
    onClose();
  };

  return (
    <DraggableModal
      title="微信扫码绑定"
      open={open}
      onCancel={handleClose}
      footer={null}
      maskClosable={false}
      destroyOnHidden
    >
      {phase === 'loading' && <Typography.Text type="secondary">正在生成二维码…</Typography.Text>}

      {phase === 'binding' && (
        <Space direction="vertical" size="middle" style={{ width: '100%', textAlign: 'center' }}>
          {qrMode === 'mock' ? (
            <Alert
              type="info"
              showIcon
              message="当前为模拟环境（fake）"
              description="真实微信扫码需配置授权凭证（服务号/网站应用就绪后切 real 即生效）。点击下方按钮模拟员工扫码完成绑定。"
            />
          ) : qrMode === 'embed' ? (
            <iframe
              src={buildWechatEmbedUrl(qrcodeUrl)}
              title="微信扫码"
              style={{ width: '100%', minHeight: 400, border: 'none' }}
            />
          ) : (
            <Space direction="vertical" size={8} style={{ alignItems: 'center', width: '100%' }}>
              <div style={{ padding: 12, background: '#fff', borderRadius: 12, border: '1px solid #e5e7e5', lineHeight: 0 }}>
                <QrcodeImage url={qrcodeUrl} size={220} />
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                二维码需用「手机微信扫一扫」扫描，在手机上完成授权确认
              </Typography.Text>
            </Space>
          )}
          <Typography.Text type="secondary">
            请引导员工使用微信「扫一扫」扫描二维码完成绑定；绑定后该员工可直接扫码登录管理端
          </Typography.Text>
          {qrMode === 'mock' && (
            <Button type="primary" onClick={() => void handleMockScan()}>
              模拟扫码完成
            </Button>
          )}
        </Space>
      )}

      {phase === 'confirmed' && (
        <Alert type="success" showIcon message="绑定成功" description="员工微信已关联，可关闭此窗口。" />
      )}

      {phase === 'expired' && (
        <Alert type="warning" showIcon message="二维码已过期" description="请关闭后重新生成二维码。" />
      )}

      {phase === 'failed' && (
        <Alert
          type="error"
          showIcon
          message="绑定失败"
          description="可能是该微信已绑定其他员工，请确认后重试。"
        />
      )}

      <div style={{ textAlign: 'right', marginTop: 16 }}>
        <Button onClick={handleClose}>{phase === 'confirmed' ? '完成' : '关闭'}</Button>
      </div>
    </DraggableModal>
  );
}
